/**
 * Captures the response body of failed (4xx/5xx) XHR requests so that
 * L9RUM's `beforeSend` can attach the actual upstream error message instead
 * of the generic SDK-generated one.
 *
 * Must be installed (installNetworkErrorBodyCapture) before L9RUM.init().
 * The returned cleanup function must be called on component unmount to
 * restore the original XHR prototype and stop the background pruning timer.
 */

const MAX_ENTRIES = 50;
const TTL_MS = 15_000; // 15 s — enough time for L9RUM beforeSend to fire

interface ErrorEntry {
  body: string;
  timestamp: number;
}

// Map guarantees insertion order so the oldest entry is always first.
const cache = new Map<string, ErrorEntry>();

function pruneStale(): void {
  const now = Date.now();
  for (const [key, entry] of cache) {
    if (now - entry.timestamp > TTL_MS) {
      cache.delete(key);
    }
  }
}

/**
 * Writes a URL → body entry, pruning stale entries first and evicting
 * the oldest entry when the cache is at capacity.
 * Using a dedicated store() function ensures pruneStale() always runs
 * before the MAX_ENTRIES check, not just lazily on reads.
 */
function store(url: string, body: string): void {
  pruneStale();
  if (cache.size >= MAX_ENTRIES) {
    // Map iterator is insertion-ordered — first key is the oldest
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
  cache.set(url, { body, timestamp: Date.now() });
}

/** Returns the captured raw response body for the given URL, or null. */
export function getErrorResponseBody(url: string): string | null {
  return cache.get(url)?.body ?? null;
}

/**
 * Parses the raw response body and returns a human-readable message.
 * Tries common server error shapes before falling back to the raw string.
 */
export function extractUpstreamMessage(rawBody: string): string {
  try {
    const parsed = JSON.parse(rawBody);
    return (
      parsed?.errorDetails?.[0]?.message ??
      parsed?.message ??
      parsed?.error ??
      parsed?.error_message ??
      parsed?.errorMessage ??
      parsed?.description ??
      rawBody.substring(0, 300)
    );
  } catch {
    return rawBody.substring(0, 300);
  }
}

/**
 * Monkey-patches XMLHttpRequest to capture response bodies for error responses.
 *
 * Returns an uninstall function that:
 *  - restores the original XHR prototype methods
 *  - stops the background pruning interval
 *  - clears the cache
 *
 * Wire the return value into a useEffect cleanup so the patch is removed
 * if the component that initialises L9RUM ever unmounts.
 */
export function installNetworkErrorBodyCapture(): () => void {
  if ((window as any).__l9ErrorBodyCaptureInstalled) {
    return () => {}; // already installed — no-op cleanup
  }
  (window as any).__l9ErrorBodyCaptureInstalled = true;

  // Store originals before patching so we can restore them on cleanup.
  const OrigOpen = XMLHttpRequest.prototype.open;
  const OrigSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method: string, url: string, ...rest: any[]) {
    (this as any).__l9CaptureUrl = typeof url === 'string' ? url : String(url);
    return OrigOpen.apply(this, [method, url, ...rest] as any);
  };

  XMLHttpRequest.prototype.send = function (...args: any[]) {
    const xhr = this as XMLHttpRequest;

    xhr.addEventListener('readystatechange', () => {
      if (xhr.readyState !== 4) return;
      if (xhr.status < 400) return;

      const url: string = (xhr as any).__l9CaptureUrl ?? '';
      if (!url) return;

      let body = '';
      try {
        body = xhr.responseText ?? '';
      } catch {
        // responseText can throw on non-text response types — ignore
      }
      if (!body) return;

      store(url, body);
    });

    return OrigSend.apply(xhr, args as any);
  };

  // Proactive background pruning — evicts stale entries even when no reads
  // are happening, preventing the cache from growing unbounded between reads.
  const pruneInterval = setInterval(() => pruneStale(), TTL_MS);

  return function uninstall(): void {
    XMLHttpRequest.prototype.open = OrigOpen;
    XMLHttpRequest.prototype.send = OrigSend;
    clearInterval(pruneInterval);
    cache.clear();
    (window as any).__l9ErrorBodyCaptureInstalled = false;
  };
}

